import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPlansByPatient, type PlanListItem } from '@/services/plans.service';
import { getQuotesByPlan, type Quote } from '@/services/quotes.service';
import { listPatientFacilities } from '@/services/patients.service';
import { listLoans } from '@/services/loans.service';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';

interface EnrollmentLinkageFieldsProps {
  patientId: number;
  planId: string;
  onPlanIdChange: (value: string) => void;
  quoteId: string;
  onQuoteIdChange: (value: string) => void;
  facilityId: string;
  onFacilityIdChange: (value: string) => void;
  selectedLoanIds?: number[];
  onSelectedLoanIdsChange?: (loanIds: number[]) => void;
  /** When true, fetches patient loans and shows multi-select checkboxes. Default false. */
  showLoans?: boolean;
  /** When false, hides the Plan and Quote selectors. Default true. */
  showPlanQuote?: boolean;
  /** When true, shows a required asterisk on the Facility label. Default false. */
  facilityRequired?: boolean;
}

// PlanListItem, which is what getPlansByPatient returns. Typed as Plan it was
// a different model; every field used here (planId, displayId, name,
// totalQuote) exists on PlanListItem.
function planLabel(plan: PlanListItem): string {
  const displayId = plan.displayId ? `Plan #${plan.displayId}` : `Plan #${plan.planId}`;
  const pieces = [
    displayId,
    plan.name?.trim() || null,
    plan.totalQuote != null ? `$${plan.totalQuote.toFixed(2)}` : null,
  ].filter(Boolean);
  return pieces.join(' - ');
}

function quoteLabel(quote: Quote): string {
  const state = quote.activeQuote ? 'Active' : 'Inactive';
  return `Quote #${quote.quoteId} - ${state} - $${quote.totalCalculated.toFixed(2)}`;
}

function formatLoanDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US');
}

export function EnrollmentLinkageFields({
  patientId,
  planId,
  onPlanIdChange,
  quoteId,
  onQuoteIdChange,
  facilityId,
  onFacilityIdChange,
  selectedLoanIds = [],
  onSelectedLoanIdsChange,
  showLoans = false,
  showPlanQuote = true,
  facilityRequired = false,
}: EnrollmentLinkageFieldsProps) {
  const selectedPlanId = Number(planId) || 0;
  const selectedQuoteId = Number(quoteId) || 0;

  const { data: plans } = useQuery({
    queryKey: ['patient-plans', patientId],
    queryFn: () => getPlansByPatient(patientId),
    enabled: patientId > 0,
    staleTime: 60_000,
  });

  const { data: quotes } = useQuery({
    queryKey: ['plan-quotes', selectedPlanId],
    queryFn: () => getQuotesByPlan(selectedPlanId),
    enabled: selectedPlanId > 0,
    staleTime: 60_000,
  });

  const { data: facilities } = useQuery({
    queryKey: ['patient-facilities'],
    queryFn: listPatientFacilities,
    staleTime: 5 * 60 * 1000,
  });

  const { data: loansResponse } = useQuery({
    queryKey: ['patient-loans', patientId],
    queryFn: () => listLoans({ patientId, perPage: 100 }),
    enabled: patientId > 0 && showLoans,
    staleTime: 60_000,
  });

  const selectedPlan = useMemo(
    () => (plans ?? []).find((plan) => plan.planId === selectedPlanId) ?? null,
    [plans, selectedPlanId],
  );

  const quoteOptions = useMemo(() => {
    const items = quotes ?? [];
    if (selectedQuoteId > 0 && !items.some((quote) => quote.quoteId === selectedQuoteId)) {
      return [
        ...items,
        {
          quoteId: selectedQuoteId,
          activeQuote: false,
          totalCalculated: 0,
        } as Quote,
      ];
    }
    return items;
  }, [quotes, selectedQuoteId]);

  const patientLoans = loansResponse?.data ?? [];

  useEffect(() => {
    if (selectedPlanId === 0 && quoteId !== '') {
      onQuoteIdChange('');
    }
  }, [onQuoteIdChange, quoteId, selectedPlanId]);

  // Global facility override pre-fills the field on first render. Once a value
  // is present, this effect short-circuits, so the existing plan-default
  // effect below still wins after the user picks a plan.
  const overrideFacilityId = useGlobalFacilityStore((s) => s.selectedFacilityIds[0] ?? null);
  useEffect(() => {
    if (overrideFacilityId == null || Number(facilityId) > 0) return;
    onFacilityIdChange(String(overrideFacilityId));
  }, [overrideFacilityId, facilityId, onFacilityIdChange]);

  useEffect(() => {
    if (!selectedPlan?.facilityId || Number(facilityId) > 0) return;
    onFacilityIdChange(String(selectedPlan.facilityId));
  }, [facilityId, onFacilityIdChange, selectedPlan]);

  return (
    <div className="space-y-4">
      {showPlanQuote && (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs font-medium text-slate-500">
            Plan
            <select
              value={planId}
              onChange={(e) => onPlanIdChange(e.target.value)}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">No linked plan</option>
              {(plans ?? []).map((plan) => (
                <option key={plan.planId} value={plan.planId}>
                  {planLabel(plan)}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-medium text-slate-500">
            Quote
            <select
              value={quoteId}
              onChange={(e) => onQuoteIdChange(e.target.value)}
              disabled={selectedPlanId === 0}
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
            >
              <option value="">No linked quote</option>
              {quoteOptions.map((quote) => (
                <option key={quote.quoteId} value={quote.quoteId}>
                  {quote.quoteId === selectedQuoteId && quote.totalCalculated === 0 && !quotes?.some((item) => item.quoteId === quote.quoteId)
                    ? `Quote #${quote.quoteId} (current)`
                    : quoteLabel(quote)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <label className="block text-xs font-medium text-slate-500">
        Facility{facilityRequired && <span className="text-red-500"> *</span>}
        <select
          value={facilityId}
          onChange={(e) => onFacilityIdChange(e.target.value)}
          className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">No linked facility</option>
          {(facilities ?? [])
            .filter((facility) => facility.active)
            .map((facility) => (
              <option key={facility.facilityId} value={facility.facilityId}>
                {facility.facilityName}
              </option>
            ))}
        </select>
        {selectedPlan?.facilityName ? (
          <p className="mt-1 text-xs text-slate-500">
            Plan default facility: {selectedPlan.facilityName}
          </p>
        ) : null}
      </label>

      {showLoans ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500">Loans</span>
            <span className="text-xs text-slate-400">{selectedLoanIds.length} selected</span>
          </div>
          <div className="max-h-40 space-y-2 overflow-auto rounded border border-slate-200 bg-white p-3">
            {patientLoans.length > 0 ? (
              patientLoans.map((loan) => {
                const checked = selectedLoanIds.includes(loan.loanId);
                const title = loan.loanType?.trim() || `Loan #${loan.loanId}`;
                const createdLabel = formatLoanDate(loan.createDate);
                return (
                  <label key={loan.loanId} className="flex items-start gap-3 rounded border border-slate-200 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        if (!onSelectedLoanIdsChange) return;
                        if (e.target.checked) {
                          onSelectedLoanIdsChange([...selectedLoanIds, loan.loanId]);
                          return;
                        }
                        onSelectedLoanIdsChange(selectedLoanIds.filter((id) => id !== loan.loanId));
                      }}
                      className="mt-1 rounded border-slate-300"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800">{title}</div>
                      <div className="text-xs text-slate-500">
                        ${loan.loanAmount.toFixed(2)}
                        {createdLabel ? ` - ${createdLabel}` : ''}
                        {loan.loanStatus ? ` - ${loan.loanStatus}` : ''}
                      </div>
                    </div>
                  </label>
                );
              })
            ) : (
              <p className="text-xs italic text-slate-400">No patient loans available.</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
